import {
  BILLING_PERIOD_IDS,
  BILLING_PERIODS,
  formatSLE,
  parseSeatCount,
  PLAN_IDS,
  PLANS,
  quote,
  type BillingPeriodId,
  type PlanId,
} from '@buakei/pricing';
import { cn } from '@buakei/ui';
import { useId, useState } from 'react';
import { Link } from 'react-router';

/** Shared pricing presentation (ADR-010). All figures come from @buakei/pricing. */

/** Validate untrusted plan / period values, e.g. from a URL. */
export function parsePlan(value: string | null): PlanId | null {
  return (PLAN_IDS as readonly string[]).includes(value ?? '') ? (value as PlanId) : null;
}

export function parsePeriod(value: string | null): BillingPeriodId | null {
  return (BILLING_PERIOD_IDS as readonly string[]).includes(value ?? '')
    ? (value as BillingPeriodId)
    : null;
}

export const signUpHref = (planId: PlanId, period: BillingPeriodId) =>
  `/sign-up?plan=${planId}&period=${period}`;

const FREQUENCY_DETAIL: Record<BillingPeriodId, string> = {
  annual: 'Annually: 12 months paid upfront',
  quarterly: 'Every 3 months: 3 months paid upfront',
  monthly: 'Monthly',
};

/** Monthly-equivalent comparison figure, then the actual amount and how often it is billed. */
export function PriceBlock({ planId, period }: { planId: PlanId; period: BillingPeriodId }) {
  const plan = PLANS[planId];
  const q = quote(planId, period);
  const unit = plan.perPerson ? '/ person / month' : '/ month';
  const perPerson = plan.perPerson ? ' per person' : '';
  return (
    <div className="flex flex-col gap-1">
      <p className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="font-display text-4xl text-navy-900">
          {formatSLE(q.monthlyEquivalentPerUnitMinor)}
        </span>
        <span className="text-sm text-muted-600">{unit}</span>
      </p>
      <p className="text-sm text-ink-900">
        {formatSLE(q.periodTotalPerUnitMinor)}
        {perPerson} billed {BILLING_PERIODS[period].frequency}.
      </p>
    </div>
  );
}

/** Law Firm team-size quote. Changes only the displayed figures. */
export function TeamSizeQuote({ period }: { period: BillingPeriodId }) {
  const inputId = useId();
  const errorId = useId();
  const [text, setText] = useState('1');
  const parsed = parseSeatCount(text);
  const perPerson = quote('firm', period);
  const total = parsed.ok ? quote('firm', period, parsed.seats) : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="text-sm font-medium text-ink-900">
          Number of people
        </label>
        <input
          id={inputId}
          inputMode="numeric"
          autoComplete="off"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-invalid={!parsed.ok}
          aria-describedby={parsed.ok ? undefined : errorId}
          className={cn(
            'h-10 w-28 rounded-md border bg-white px-3 text-ink-900',
            parsed.ok ? 'border-line-200' : 'border-danger-700',
          )}
        />
        {!parsed.ok ? (
          <p id={errorId} className="text-sm text-danger-700">
            {parsed.message}
          </p>
        ) : null}
      </div>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-muted-600">Price per person</dt>
        <dd className="text-right text-ink-900">{formatSLE(perPerson.periodTotalPerUnitMinor)}</dd>
        <dt className="text-muted-600">People</dt>
        <dd className="text-right text-ink-900">{parsed.ok ? parsed.seats : '—'}</dd>
        <dt className="text-muted-600">Billed</dt>
        <dd className="text-right text-ink-900">{FREQUENCY_DETAIL[period]}</dd>
        <dt className="font-medium text-navy-900">Total payable</dt>
        <dd className="text-right font-semibold text-navy-900" aria-live="polite">
          {total ? formatSLE(total.periodTotalMinor) : '—'}
        </dd>
      </dl>
    </div>
  );
}

/**
 * The plan chosen on the pricing page, shown at sign-up. Payment is not operational: this
 * quotes only, and creating an account starts no subscription (BILLING-AND-CREDITS.md).
 */
export function SelectedPlan({ planId, period }: { planId: PlanId; period: BillingPeriodId }) {
  const plan = PLANS[planId];
  return (
    <section
      aria-labelledby="selected-plan"
      className="flex flex-col gap-4 rounded-lg border border-line-200 bg-white p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="selected-plan" className="text-lg font-semibold text-navy-900">
          Your plan: {plan.name}
        </h2>
        <Link to="/pricing" className="text-sm font-medium text-navy-900 underline">
          Change plan
        </Link>
      </div>
      <PriceBlock planId={planId} period={period} />
      {plan.perPerson ? <TeamSizeQuote period={period} /> : null}
      <p className="text-sm leading-relaxed text-ink-900">
        Paid plans are not available yet. Creating an account does not start a subscription or take
        any payment.
      </p>
    </section>
  );
}
